import React, { useState, useRef } from "react";
import {
  IconPrinter,
  IconX,
  IconReceipt,
  IconFileText,
  IconDownload,
  IconCash,
  IconBuildingStore,
  IconUserCheck,
  IconCalendar,
  IconCheck,
  IconAlertTriangle,
  IconArrowDownRight,
  IconArrowUpRight
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import { useStoreSettings } from "../controllers/settings.controller";
import { getUserDetailsInLocalStorage } from "../helpers/UserDetails";

export default function FinancialReportsPrintModal({
  isOpen,
  onClose,
  reportType = "sales", // 'sales' | 'purchase' | 'expenses' | 'register' | 'shift_open' | 'shift_close' | 'shift_full'
  data = {},
  selectedShift = null,
  selectedRegister = null,
  dateRange = { from: "", to: "" }
}) {
  const [format, setFormat] = useState("a4"); // 'a4' | '80mm'
  const { data: storeSettings } = useStoreSettings();
  const currentUser = getUserDetailsInLocalStorage() || {};

  if (!isOpen) return null;

  const storeName = storeSettings?.storeName || storeSettings?.name || "RESTAURANT & BAR";
  const storeAddress = storeSettings?.address || "";
  const storePhone = storeSettings?.phone || "";
  const storeEmail = storeSettings?.email || "";
  const storePan = storeSettings?.panNo || storeSettings?.pan_no || storeSettings?.taxId || "";
  const currency = storeSettings?.currency || "Rs.";

  // Profile-based staff name
  const staffProfileName = currentUser?.name || currentUser?.email || "Authorized Staff";
  const staffDesignation = currentUser?.designation || currentUser?.role || "Cashier / Operator";

  const handlePrint = () => {
    window.print();
  };

  // Helper to safely format numbers
  const fmt = (v) => {
    const num = Number(String(v || 0).replace(/,/g, ""));
    return isNaN(num) ? "0.00" : num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Safe JSON / Array extractor for denominations
  const parseDenoms = (d) => {
    if (!d) return [];
    let raw = d;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch (e) {
        return [];
      }
    }
    if (!raw) return [];
    if (Array.isArray(raw)) {
      if (raw.length > 0 && (raw[0].breakdown || raw[0].items)) {
        return raw;
      }
      if (raw.length > 0 && raw[0].denomination !== undefined) {
        const subtotal = raw.reduce((sum, it) => sum + (Number(it.denomination) * Number(it.count || 0)), 0);
        return [{
          currency_code: currency,
          is_base: true,
          exchange_rate: 1,
          currency_total: subtotal,
          base_total: subtotal,
          breakdown: raw.map(it => ({
            denomination: Number(it.denomination),
            count: Number(it.count || 0),
            total: Number(it.total || (Number(it.denomination) * Number(it.count || 0)))
          }))
        }];
      }
      return raw;
    }
    if (typeof raw === "object") {
      const keys = Object.keys(raw);
      if (keys.length === 0) return [];
      const firstVal = raw[keys[0]];
      const isNestedCurrency = typeof firstVal === "object" && firstVal !== null && !Array.isArray(firstVal);
      if (isNestedCurrency) {
        return keys.map(currKey => {
          const breakdownObj = raw[currKey] || {};
          const breakdown = Object.entries(breakdownObj)
            .map(([denom, count]) => ({
              denomination: Number(denom),
              count: Number(count || 0),
              total: Number(denom) * Number(count || 0)
            }))
            .filter(item => item.count > 0 || item.denomination > 0)
            .sort((a, b) => b.denomination - a.denomination);

          const subtotal = breakdown.reduce((acc, it) => acc + it.total, 0);
          return {
            currency_code: currKey,
            exchange_rate: 1,
            is_base: currKey === currency || currKey === "NPR",
            currency_total: subtotal,
            base_total: subtotal,
            breakdown
          };
        });
      } else {
        const breakdown = Object.entries(raw)
          .map(([denom, count]) => ({
            denomination: Number(denom),
            count: Number(count || 0),
            total: Number(denom) * Number(count || 0)
          }))
          .filter(item => item.count > 0 || item.denomination > 0)
          .sort((a, b) => b.denomination - a.denomination);

        const subtotal = breakdown.reduce((acc, it) => acc + it.total, 0);
        return [{
          currency_code: currency,
          exchange_rate: 1,
          is_base: true,
          currency_total: subtotal,
          base_total: subtotal,
          breakdown
        }];
      }
    }
    return [];
  };

  // Format date time safely
  const formatDateTime = (dt) => {
    if (!dt) return "-";
    try {
      const d = new Date(dt);
      return isNaN(d.getTime()) ? String(dt) : d.toLocaleString("en-GB", {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: true
      });
    } catch (e) {
      return String(dt);
    }
  };

  // RENDER REPORT CONTENT ACCORDING TO TYPE
  const renderReportContent = () => {
    switch (reportType) {
      case "shift_open":
        return renderShiftOpenReport();
      case "shift_close":
      case "shift_full":
        return renderShiftCloseReport();
      case "register":
        return renderRegisterReport();
      case "purchase":
        return renderPurchaseReport();
      case "expenses":
        return renderExpensesReport();
      case "sales":
      default:
        return renderSalesReport();
    }
  };

  // ==========================================
  // 1. SHIFT OPEN REPORT (FLOAT & DENOMINATIONS)
  // ==========================================
  const renderShiftOpenReport = () => {
    const shift = selectedShift || data?.shift || data?.currentShift || {};
    const openDenoms = parseDenoms(shift.start_denominations || shift.denominations);
    const openingFloat = Number(shift.opening_balance || shift.opening_cash || 0);
    const cashierName = shift.cashier_name || shift.opened_by_name || shift.opened_by || staffProfileName;

    return (
      <div className="report-paper">
        {/* Header */}
        <div className="text-center pb-3 border-b-2 border-dashed border-gray-400 mb-4">
          <h2 className="text-xl font-black uppercase tracking-wider text-black">{storeName}</h2>
          {storeAddress && <p className="text-xs text-gray-700">{storeAddress}</p>}
          <div className="flex justify-center gap-3 text-xs text-gray-700 mt-0.5">
            {storePhone && <span>Tel: {storePhone}</span>}
            {storePan && <span>PAN/VAT: {storePan}</span>}
          </div>
          <div className="mt-2.5 inline-block px-3 py-1 bg-black text-white text-xs font-black uppercase tracking-widest rounded-xs">
            SHIFT OPEN REPORT (FLOAT AUDIT)
          </div>
        </div>

        {/* Shift & Staff Metadata */}
        <div className="grid grid-cols-2 gap-2 text-xs border-b border-gray-300 pb-3 mb-3">
          <div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Shift Number:</span><span className="font-bold text-black">Shift #{shift.shift_number || shift.id || "1"}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Shift ID:</span><span className="font-mono text-black">#{shift.id || "-"}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Status:</span><span className="font-bold text-emerald-700 uppercase">ACTIVE / OPEN</span></div>
          </div>
          <div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Opened At:</span><span className="font-bold text-black">{formatDateTime(shift.opened_at || new Date())}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Assigned Cashier:</span><span className="font-bold text-black">{cashierName}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Logged Staff (Profile):</span><span className="font-bold text-indigo-900">{staffProfileName}</span></div>
          </div>
        </div>

        {/* Opening Balance Hero */}
        <div className="bg-emerald-50 border border-emerald-300 rounded p-3 mb-4 flex justify-between items-center">
          <div>
            <span className="text-xs uppercase font-bold text-emerald-900 tracking-wider">Declared Opening Cash Float</span>
            <div className="text-2xs text-gray-600">Handed over to cash drawer at shift initiation</div>
          </div>
          <div className="text-xl font-black text-emerald-900">{currency} {fmt(openingFloat)}</div>
        </div>

        {/* Multi-Currency & Physical Denominations Table */}
        <div className="mb-4">
          <div className="text-xs font-black uppercase tracking-wider text-black mb-1.5 flex items-center justify-between border-b pb-1">
            <span>Physical Cash Float Denominations</span>
            <span className="text-2xs font-normal text-gray-600">Count Verified</span>
          </div>

          {openDenoms && openDenoms.length > 0 ? (
            <div className="space-y-2">
              {openDenoms.map((currGrp, gIdx) => {
                const currencyCode = currGrp.currency_code || currGrp.currency || currency;
                const isBase = currGrp.is_base !== false;
                const rate = Number(currGrp.exchange_rate) || 1;
                const breakdown = currGrp.breakdown || currGrp.items || [];

                return (
                  <div key={gIdx} className="border border-gray-300 rounded overflow-hidden">
                    <div className="bg-gray-100 px-2 py-1 text-2xs font-bold flex justify-between border-b text-gray-800">
                      <span>Currency: {currencyCode} {!isBase && `(Rate: 1 ${currencyCode} = ${rate} Base)`}</span>
                      <span>Subtotal: {currencyCode} {fmt(currGrp.currency_total || currGrp.base_total || 0)}</span>
                    </div>
                    <table className="w-full text-xs text-left">
                      <thead>
                        <tr className="border-b bg-gray-50 text-2xs text-gray-600">
                          <th className="py-1 px-2">Denomination</th>
                          <th className="py-1 px-2 text-center">Count / Units</th>
                          <th className="py-1 px-2 text-right">Amount ({currencyCode})</th>
                          {!isBase && <th className="py-1 px-2 text-right">Base Eq. ({currency})</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {breakdown.map((item, iIdx) => (
                          <tr key={iIdx} className="border-b border-gray-100">
                            <td className="py-1 px-2 font-mono">{currencyCode} {fmt(item.denomination)}</td>
                            <td className="py-1 px-2 text-center font-bold">{item.count || 0}</td>
                            <td className="py-1 px-2 text-right font-mono">{fmt(item.total || (item.denomination * (item.count || 0)))}</td>
                            {!isBase && <td className="py-1 px-2 text-right font-mono">{fmt((item.total || (item.denomination * (item.count || 0))) * rate)}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-3 text-center border border-dashed rounded text-xs text-gray-600 bg-gray-50">
              No itemized denominations recorded. Lump sum opening float of <strong>{currency} {fmt(openingFloat)}</strong> verified.
            </div>
          )}
        </div>

        {/* Shift Notes */}
        {shift.notes && (
          <div className="text-xs bg-gray-50 border rounded p-2 mb-4">
            <span className="font-bold text-gray-700">Opening Note: </span>
            <span className="text-gray-800">{shift.notes}</span>
          </div>
        )}

        {/* Dual Signatures */}
        <div className="grid grid-cols-2 gap-8 pt-8 mt-6 border-t border-gray-300 text-xs">
          <div className="text-center">
            <div className="border-b border-black mb-1 w-3/4 mx-auto"></div>
            <div className="font-bold text-black">{cashierName}</div>
            <div className="text-2xs text-gray-600">Opening Cashier Signature</div>
          </div>
          <div className="text-center">
            <div className="border-b border-black mb-1 w-3/4 mx-auto"></div>
            <div className="font-bold text-black">{staffProfileName}</div>
            <div className="text-2xs text-gray-600">Supervisor / Duty Manager ({staffDesignation})</div>
          </div>
        </div>

        <div className="text-center text-3xs text-gray-500 mt-6 pt-2 border-t border-dashed">
          Report Generated: {new Date().toLocaleString()} | Terminal: POS-01 | {storeName}
        </div>
      </div>
    );
  };

  // ==========================================
  // 2. SHIFT CLOSE REPORT (FULL Z-REPORT)
  // ==========================================
  const renderShiftCloseReport = () => {
    const shift = selectedShift || data?.shift || data?.currentShift || {};
    const stats = shift.current_stats || shift.stats || shift;
    
    // Denominations
    const openDenoms = parseDenoms(shift.start_denominations);
    const closeDenoms = parseDenoms(shift.denominations);

    // Financial Values
    const openingFloat = Number(shift.opening_balance || shift.opening_cash || 0);
    const grossSales = Number(stats.gross_sales || stats.system_total_sales || 0);
    const netSales = Number(stats.net_sales || stats.system_total_sales || 0);
    const totalDiscount = Number(stats.total_discount || stats.discount || 0);
    const totalTax = Number(stats.tax_total || stats.vat || 0);
    const roundOff = Number(stats.round_off || 0);
    const totalBilled = Number(stats.bill_total || grossSales);
    const totalInvoices = Number(stats.total_invoices || 0);

    // Collections Breakdown
    const paymentBreakdown = Array.isArray(stats.payment_breakdown) 
      ? stats.payment_breakdown 
      : (Array.isArray(shift.payment_breakdown) 
        ? shift.payment_breakdown 
        : (Array.isArray(shift.payments) ? shift.payments : []));
    const cashSales = Number(stats.system_cash_sales ?? stats.cash_sales ?? 0);
    const qrSales = Number(stats.system_qr_sales ?? stats.qr_sales ?? 0);
    const cardSales = Number(stats.system_card_sales ?? stats.card_sales ?? 0);
    const otherSales = Number(stats.system_other_sales ?? stats.other_sales ?? 0);

    // Credit Dues, Expenses, Purchases, Past Dues
    const duesCreated = Number(stats.dues_created ?? stats.dues ?? 0);
    const totalExpenses = Number(stats.system_expenses ?? stats.expenses ?? 0);
    const cashExpenses = Number(stats.system_cash_expenses ?? stats.cash_expenses ?? 0);
    const totalPurchases = Number(stats.system_purchase_payments ?? stats.purchases ?? 0);
    const cashPurchases = Number(stats.system_cash_purchases ?? stats.cash_purchases ?? 0);
    
    // Past Dues
    const pastDueCollected = Number(stats.system_due_collected ?? stats.due_collected ?? 0);
    const cashPastDueCollected = Number(stats.system_cash_due_collected ?? pastDueCollected);
    const pastDuePaid = Number(stats.system_due_paid ?? stats.due_paid ?? 0);
    const cashPastDuePaid = Number(stats.system_cash_due_paid ?? pastDuePaid);

    // Cash Reconciliation
    const expectedCash = Number(stats.system_expected_cash ?? stats.expected_cash ?? 0);
    const actualCash = Number(shift.closing_balance || shift.actual_cash_in_hand || stats.actual_cash_in_hand || 0);
    const cashDiff = actualCash - expectedCash;

    const cashierName = shift.cashier_name || shift.opened_by_name || shift.opened_by || staffProfileName;
    const closedByName = shift.closed_by_name || shift.closed_by || staffProfileName;

    return (
      <div className="report-paper">
        {/* Header */}
        <div className="text-center pb-3 border-b-2 border-dashed border-gray-400 mb-3">
          <h2 className="text-xl font-black uppercase tracking-wider text-black">{storeName}</h2>
          {storeAddress && <p className="text-xs text-gray-700">{storeAddress}</p>}
          <div className="flex justify-center gap-3 text-xs text-gray-700 mt-0.5">
            {storePhone && <span>Tel: {storePhone}</span>}
            {storePan && <span>PAN/VAT: {storePan}</span>}
          </div>
          <div className="mt-2 inline-block px-3 py-1 bg-black text-white text-xs font-black uppercase tracking-widest rounded-xs">
            SHIFT CLOSE Z-REPORT (AUDIT STATEMENT)
          </div>
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-2 gap-2 text-xs border-b border-gray-300 pb-2.5 mb-3">
          <div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Shift Number:</span><span className="font-bold text-black">Shift #{shift.shift_number || shift.id || "1"}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Shift ID:</span><span className="font-mono text-black">#{shift.id || "-"}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Opened At:</span><span>{formatDateTime(shift.opened_at)}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Closed At:</span><span className="font-bold text-black">{formatDateTime(shift.closed_at || new Date())}</span></div>
          </div>
          <div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Opened By:</span><span className="font-bold text-black">{cashierName}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Closed By:</span><span className="font-bold text-black">{closedByName}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Settled By (Profile):</span><span className="font-bold text-indigo-900">{staffProfileName}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Total Invoices:</span><span className="font-bold text-black">{totalInvoices} bills</span></div>
          </div>
        </div>

        {/* 1. SALES REVENUE BREAKDOWN */}
        <div className="mb-3">
          <div className="text-xs font-black uppercase tracking-wider text-black border-b border-gray-300 pb-1 mb-1.5 flex justify-between">
            <span>1. Sales Revenue Summary</span>
            <span className="text-2xs font-normal text-gray-500">Gross to Net</span>
          </div>
          <table className="w-full text-xs">
            <tbody>
              <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">Gross Sales (Items Total)</td><td className="py-1 text-right font-mono">{currency} {fmt(grossSales)}</td></tr>
              <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">Total Discounts</td><td className="py-1 text-right font-mono text-rose-600">-{currency} {fmt(totalDiscount)}</td></tr>
              <tr className="border-b border-gray-100 font-bold bg-gray-50"><td className="py-1 text-black">Net Sales</td><td className="py-1 text-right font-mono text-black">{currency} {fmt(netSales)}</td></tr>
              <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">Taxes (VAT / Service Charge)</td><td className="py-1 text-right font-mono">+{currency} {fmt(totalTax)}</td></tr>
              {roundOff !== 0 && <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">Round Off Adjustment</td><td className="py-1 text-right font-mono">{currency} {fmt(roundOff)}</td></tr>}
              <tr className="font-black border-t border-black bg-gray-100"><td className="py-1.5 text-black">TOTAL BILLED SALES</td><td className="py-1.5 text-right font-mono text-black">{currency} {fmt(totalBilled)}</td></tr>
            </tbody>
          </table>
        </div>

        {/* 2. COLLECTIONS BY PAYMENT METHOD */}
        <div className="mb-3">
          <div className="text-xs font-black uppercase tracking-wider text-black border-b border-gray-300 pb-1 mb-1.5 flex justify-between">
            <span>2. Collections by Payment Type</span>
            <span className="text-2xs font-normal text-gray-500">Payment Modes</span>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-2xs text-gray-600 bg-gray-50">
                <th className="py-1 text-left">Payment Mode</th>
                <th className="py-1 text-right">Direct Sales</th>
                <th className="py-1 text-right">Past Dues / Net</th>
              </tr>
            </thead>
            <tbody>
              {paymentBreakdown && paymentBreakdown.length > 0 ? (
                paymentBreakdown.map((pm, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="py-1 font-medium text-black">{pm.payment_method || "Payment"}</td>
                    <td className="py-1 text-right font-mono">{currency} {fmt(pm.total_amount)}</td>
                    <td className="py-1 text-right font-mono font-bold">{currency} {fmt(pm.net_amount ?? pm.total_amount)}</td>
                  </tr>
                ))
              ) : (
                <>
                  <tr className="border-b border-gray-100"><td className="py-1">Cash Collection</td><td className="py-1 text-right font-mono">{currency} {fmt(cashSales)}</td><td className="py-1 text-right font-mono font-bold">{currency} {fmt(cashSales)}</td></tr>
                  <tr className="border-b border-gray-100"><td className="py-1">QR / Digital Collection</td><td className="py-1 text-right font-mono">{currency} {fmt(qrSales)}</td><td className="py-1 text-right font-mono font-bold">{currency} {fmt(qrSales)}</td></tr>
                  <tr className="border-b border-gray-100"><td className="py-1">Card / POS Terminal</td><td className="py-1 text-right font-mono">{currency} {fmt(cardSales)}</td><td className="py-1 text-right font-mono font-bold">{currency} {fmt(cardSales)}</td></tr>
                  {otherSales > 0 && <tr className="border-b border-gray-100"><td className="py-1">Other Payment Methods</td><td className="py-1 text-right font-mono">{currency} {fmt(otherSales)}</td><td className="py-1 text-right font-mono font-bold">{currency} {fmt(otherSales)}</td></tr>}
                </>
              )}
              {duesCreated > 0 && (
                <tr className="border-b border-amber-200 bg-amber-50/50 text-amber-900">
                  <td className="py-1 font-medium">New Credit / Due Created (Unpaid)</td>
                  <td className="py-1 text-right font-mono text-amber-700">{currency} {fmt(duesCreated)}</td>
                  <td className="py-1 text-right font-mono font-bold text-amber-800">Pending</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 3. DUES, EXPENSES, PURCHASES & PAST DUES RECOVERY */}
        <div className="mb-3">
          <div className="text-xs font-black uppercase tracking-wider text-black border-b border-gray-300 pb-1 mb-1.5">
            3. Credit Dues, Expenses & Supplier Purchases
          </div>
          <table className="w-full text-xs">
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-1 text-gray-700">New Customer Credit Dues Created</td>
                <td className="py-1 text-right font-mono font-medium text-amber-800">{currency} {fmt(duesCreated)}</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-1 text-gray-700">Past Customer Dues Collected / Recovered</td>
                <td className="py-1 text-right font-mono font-medium text-emerald-800">+{currency} {fmt(pastDueCollected)} (Cash: {currency} {fmt(cashPastDueCollected)})</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-1 text-gray-700">Expenses Paid Out During Shift</td>
                <td className="py-1 text-right font-mono font-medium text-rose-800">-{currency} {fmt(totalExpenses)} (Cash: {currency} {fmt(cashExpenses)})</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-1 text-gray-700">Supplier Purchases Paid During Shift</td>
                <td className="py-1 text-right font-mono font-medium text-rose-800">-{currency} {fmt(totalPurchases)} (Cash: {currency} {fmt(cashPurchases)})</td>
              </tr>
              {pastDuePaid > 0 && (
                <tr className="border-b border-gray-100">
                  <td className="py-1 text-gray-700">Past Supplier Dues Paid</td>
                  <td className="py-1 text-right font-mono font-medium text-rose-800">-{currency} {fmt(pastDuePaid)} (Cash: {currency} {fmt(cashPastDuePaid)})</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 4. CASH DRAWER AUDIT & RECONCILIATION */}
        <div className="mb-4 border-2 border-black rounded p-3 bg-gray-50">
          <div className="text-xs font-black uppercase tracking-wider text-black border-b border-gray-300 pb-1 mb-2">
            4. Cash Drawer Reconciliation Statement
          </div>
          <table className="w-full text-xs">
            <tbody>
              <tr><td className="py-0.5 text-gray-700">(+) Starting Opening Float</td><td className="py-0.5 text-right font-mono">{currency} {fmt(openingFloat)}</td></tr>
              <tr><td className="py-0.5 text-gray-700">(+) Shift Cash Sales Collected</td><td className="py-0.5 text-right font-mono">+{currency} {fmt(cashSales)}</td></tr>
              {cashPastDueCollected > 0 && <tr><td className="py-0.5 text-gray-700">(+) Past Dues Collected in Cash</td><td className="py-0.5 text-right font-mono">+{currency} {fmt(cashPastDueCollected)}</td></tr>}
              {cashExpenses > 0 && <tr><td className="py-0.5 text-rose-700">(-) Drawer Cash Expenses Paid</td><td className="py-0.5 text-right font-mono text-rose-700">-{currency} {fmt(cashExpenses)}</td></tr>}
              {cashPurchases > 0 && <tr><td className="py-0.5 text-rose-700">(-) Drawer Cash Purchases Paid</td><td className="py-0.5 text-right font-mono text-rose-700">-{currency} {fmt(cashPurchases)}</td></tr>}
              {cashPastDuePaid > 0 && <tr><td className="py-0.5 text-rose-700">(-) Past Supplier Dues Paid (Cash)</td><td className="py-0.5 text-right font-mono text-rose-700">-{currency} {fmt(cashPastDuePaid)}</td></tr>}
              
              <tr className="border-t border-gray-300 font-bold bg-white"><td className="py-1 text-black">SYSTEM EXPECTED CASH IN DRAWER</td><td className="py-1 text-right font-mono text-black">{currency} {fmt(expectedCash)}</td></tr>
              <tr className="font-bold bg-white"><td className="py-1 text-black">ACTUAL PHYSICAL CASH COUNTED</td><td className="py-1 text-right font-mono text-black">{currency} {fmt(actualCash)}</td></tr>
              
              <tr className={`border-t-2 font-black ${Math.abs(cashDiff) < 0.01 ? "text-emerald-800 bg-emerald-50" : cashDiff > 0 ? "text-blue-900 bg-blue-50" : "text-rose-900 bg-rose-50"}`}>
                <td className="py-1.5 uppercase">
                  CASH VARIANCE ({Math.abs(cashDiff) < 0.01 ? "EXACT MATCH / BALANCED" : cashDiff > 0 ? "OVER / SURPLUS" : "SHORT / DEFICIT"})
                </td>
                <td className="py-1.5 text-right font-mono text-sm">
                  {cashDiff > 0 ? `+${currency} ${fmt(cashDiff)}` : `${currency} ${fmt(cashDiff)}`}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 5. CLOSING PHYSICAL DENOMINATIONS TABLE */}
        <div className="mb-4">
          <div className="text-xs font-black uppercase tracking-wider text-black border-b border-gray-300 pb-1 mb-1.5 flex justify-between">
            <span>5. Closing Cash Denominations Count</span>
            <span className="text-2xs font-normal text-gray-500">Physical Register Drawer Count</span>
          </div>

          {closeDenoms && closeDenoms.length > 0 ? (
            <div className="space-y-2">
              {closeDenoms.map((currGrp, gIdx) => {
                const currencyCode = currGrp.currency_code || currGrp.currency || currency;
                const isBase = currGrp.is_base !== false;
                const rate = Number(currGrp.exchange_rate) || 1;
                const breakdown = currGrp.breakdown || currGrp.items || [];

                return (
                  <div key={gIdx} className="border border-gray-300 rounded overflow-hidden">
                    <div className="bg-gray-100 px-2 py-1 text-2xs font-bold flex justify-between border-b text-gray-800">
                      <span>Currency: {currencyCode} {!isBase && `(1 ${currencyCode} = ${rate} Base)`}</span>
                      <span>Total: {currencyCode} {fmt(currGrp.currency_total || currGrp.base_total || 0)}</span>
                    </div>
                    <table className="w-full text-xs text-left">
                      <thead>
                        <tr className="border-b bg-gray-50 text-2xs text-gray-600">
                          <th className="py-1 px-2">Denomination</th>
                          <th className="py-1 px-2 text-center">Count</th>
                          <th className="py-1 px-2 text-right">Amount ({currencyCode})</th>
                          {!isBase && <th className="py-1 px-2 text-right">Base Eq. ({currency})</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {breakdown.map((item, iIdx) => (
                          <tr key={iIdx} className="border-b border-gray-100">
                            <td className="py-1 px-2 font-mono">{currencyCode} {fmt(item.denomination)}</td>
                            <td className="py-1 px-2 text-center font-bold">{item.count || 0}</td>
                            <td className="py-1 px-2 text-right font-mono">{fmt(item.total || (item.denomination * (item.count || 0)))}</td>
                            {!isBase && <td className="py-1 px-2 text-right font-mono">{fmt((item.total || (item.denomination * (item.count || 0))) * rate)}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-2.5 text-center border border-dashed rounded text-xs text-gray-600 bg-gray-50">
              Closing cash recorded as lump sum: <strong>{currency} {fmt(actualCash)}</strong>.
            </div>
          )}
        </div>

        {/* Dual Signature Blocks */}
        <div className="grid grid-cols-2 gap-8 pt-8 mt-6 border-t border-gray-400 text-xs">
          <div className="text-center">
            <div className="border-b border-black mb-1 w-3/4 mx-auto"></div>
            <div className="font-bold text-black">{closedByName}</div>
            <div className="text-2xs text-gray-600">Closing Cashier Signature</div>
          </div>
          <div className="text-center">
            <div className="border-b border-black mb-1 w-3/4 mx-auto"></div>
            <div className="font-bold text-black">{staffProfileName}</div>
            <div className="text-2xs text-gray-600">Audit Verified By ({staffDesignation})</div>
          </div>
        </div>

        <div className="text-center text-3xs text-gray-500 mt-6 pt-2 border-t border-dashed">
          Official Shift Z-Report | Generated: {new Date().toLocaleString()} | Operator Profile: {staffProfileName}
        </div>
      </div>
    );
  };

  // ==========================================
  // 3. SALES AUDIT REPORT
  // ==========================================
  const renderSalesReport = () => {
    const invSummary = data?.invoiceSummary || {};
    const rawSalesPayments = data?.sales_payments || data?.salesPayments || data?.payments || [];
    const transactions = data?.transactions || data?.detailedInvoices || data?.invoicesList || [];

    const grossSales = Number(invSummary.total_gross || 0);
    const discount = Number(invSummary.total_discount || 0);
    const netSales = Number(invSummary.net_sales || (grossSales - discount));
    const tax = Number(invSummary.total_tax || 0);
    const serviceCharge = Number(invSummary.total_service_charge || 0);
    const roundOff = Number(invSummary.total_round_off || 0);
    const complementary = Number(invSummary.total_complementary || 0);
    const totalBilled = Number(invSummary.total_paid || 0) + Number(invSummary.total_due || 0) || Number(invSummary.total_billed || netSales + tax);
    const duesCreated = Number(invSummary.total_due || data?.total_sales_due || 0);

    // Robust Payment Methods Aggregator
    const paymentMap = new Map();

    if (Array.isArray(rawSalesPayments) && rawSalesPayments.length > 0) {
      rawSalesPayments.forEach(p => {
        const title = (p.payment_method || p.title || p.name || 'Payment').trim();
        const amt = Number(p.total_amount !== undefined ? p.total_amount : (p.amount !== undefined ? p.amount : (p.total !== undefined ? p.total : 0)));
        if (title) {
          const key = title.toLowerCase();
          const existing = paymentMap.get(key) || { payment_method: title, total_amount: 0, count: 0 };
          existing.total_amount += amt;
          existing.count += (p.count || p.tx_count || 1);
          paymentMap.set(key, existing);
        }
      });
    }

    // Fallback: If total from payments is 0 and transactions exist, aggregate from transactions
    let currentTotalFromMap = Array.from(paymentMap.values()).reduce((sum, p) => sum + p.total_amount, 0);
    if (currentTotalFromMap === 0 && Array.isArray(transactions) && transactions.length > 0) {
      transactions.forEach(t => {
        const paid = Number(t.paid_amount !== undefined ? t.paid_amount : (Number(t.due_amount || 0) === 0 ? t.total : 0));
        if (paid > 0) {
          const title = (t.payment_method_name || t.payment_method || 'Cash').trim();
          const key = title.toLowerCase();
          const existing = paymentMap.get(key) || { payment_method: title, total_amount: 0, count: 0 };
          existing.total_amount += paid;
          existing.count += 1;
          paymentMap.set(key, existing);
        }
      });
    }

    const consolidatedPayments = Array.from(paymentMap.values());

    // Calculate Category Totals
    let totalCashCollected = Number(data?.total_cash_collected || 0);
    let totalQrCollected = Number(data?.total_qr_collected || 0);
    let totalCardCollected = Number(data?.total_card_collected || 0);
    let totalOtherCollected = Number(data?.total_other_collected || 0);
    let totalCollectedReceipts = Number(data?.total_collected || 0);

    if (totalCashCollected === 0 && totalQrCollected === 0 && consolidatedPayments.length > 0) {
      totalCashCollected = 0;
      totalQrCollected = 0;
      totalCardCollected = 0;
      totalOtherCollected = 0;
      totalCollectedReceipts = 0;

      consolidatedPayments.forEach(p => {
        const amt = Number(p.total_amount || 0);
        totalCollectedReceipts += amt;
        const key = (p.payment_method || '').toLowerCase();
        if (key.includes('cash')) {
          totalCashCollected += amt;
        } else if (key.includes('qr') || key.includes('fonepay') || key.includes('esewa') || key.includes('khalti') || key.includes('online') || key.includes('upi')) {
          totalQrCollected += amt;
        } else if (key.includes('card') || key.includes('pos') || key.includes('visa') || key.includes('master')) {
          totalCardCollected += amt;
        } else {
          totalOtherCollected += amt;
        }
      });
    } else if (totalCollectedReceipts === 0) {
      totalCollectedReceipts = totalCashCollected + totalQrCollected + totalCardCollected + totalOtherCollected;
    }

    return (
      <div className="report-paper">
        {/* Header */}
        <div className="text-center pb-3 border-b-2 border-dashed border-gray-400 mb-4">
          <h2 className="text-xl font-black uppercase tracking-wider text-black">{storeName}</h2>
          {storeAddress && <p className="text-xs text-gray-700">{storeAddress}</p>}
          <div className="flex justify-center gap-3 text-xs text-gray-700 mt-0.5">
            {storePhone && <span>Tel: {storePhone}</span>}
            {storePan && <span>PAN/VAT: {storePan}</span>}
          </div>
          <div className="mt-2.5 inline-block px-3 py-1 bg-black text-white text-xs font-black uppercase tracking-widest rounded-xs">
            EXECUTIVE SALES AUDIT REPORT
          </div>
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-2 gap-2 text-xs border-b border-gray-300 pb-2.5 mb-3">
          <div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Audit Period:</span><span className="font-bold text-black">{dateRange.from || "Today"} {dateRange.to && dateRange.to !== dateRange.from ? `to ${dateRange.to}` : ""}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Total Transactions:</span><span className="font-bold text-black">{invSummary.total_invoices || transactions.length || 0} Bills</span></div>
          </div>
          <div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Certified By (Profile):</span><span className="font-bold text-indigo-900">{staffProfileName}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Staff Designation:</span><span>{staffDesignation}</span></div>
          </div>
        </div>

        {/* Financial Highlights Grid - 6 Key Indicators */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4 text-center">
          <div className="bg-gray-100 p-2 rounded border">
            <div className="text-3xs uppercase font-bold text-gray-600">Gross Sales</div>
            <div className="text-sm font-black text-black">{currency} {fmt(grossSales)}</div>
          </div>
          <div className="bg-gray-100 p-2 rounded border">
            <div className="text-3xs uppercase font-bold text-gray-600">Total Discounts</div>
            <div className="text-sm font-black text-rose-600">-{currency} {fmt(discount)}</div>
          </div>
          <div className="bg-blue-50 p-2 rounded border border-blue-300">
            <div className="text-3xs uppercase font-bold text-blue-800">Total Billed</div>
            <div className="text-sm font-black text-blue-900">{currency} {fmt(totalBilled)}</div>
          </div>

          {/* PAYMENT HIGHLIGHT CARDS (PROMINENT CASH & QR METRICS) */}
          <div className="bg-emerald-50 p-2 rounded border-2 border-emerald-500 shadow-xs">
            <div className="text-3xs uppercase font-black text-emerald-800 flex items-center justify-center gap-1">
              💵 Total Cash Collected
            </div>
            <div className="text-base font-black text-emerald-900 mt-0.5">
              {currency} {fmt(totalCashCollected)}
            </div>
          </div>
          <div className="bg-indigo-50 p-2 rounded border-2 border-indigo-500 shadow-xs">
            <div className="text-3xs uppercase font-black text-indigo-800 flex items-center justify-center gap-1">
              📱 Total QR / Online
            </div>
            <div className="text-base font-black text-indigo-900 mt-0.5">
              {currency} {fmt(totalQrCollected)}
            </div>
          </div>
          <div className="bg-amber-50 p-2 rounded border border-amber-300">
            <div className="text-3xs uppercase font-bold text-amber-800">Sales Due (Unpaid)</div>
            <div className="text-sm font-black text-amber-900">{currency} {fmt(duesCreated)}</div>
          </div>
        </div>

        {/* Detailed Sales Summary Table */}
        <div className="mb-4">
          <div className="text-xs font-black uppercase tracking-wider text-black border-b border-gray-300 pb-1 mb-1.5">
            Sales & Tax Breakdown
          </div>
          <table className="w-full text-xs">
            <tbody>
              <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">Gross Sales Subtotal</td><td className="py-1 text-right font-mono">{currency} {fmt(grossSales)}</td></tr>
              <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">Concessions / Discounts</td><td className="py-1 text-right font-mono text-rose-600">-{currency} {fmt(discount)}</td></tr>
              <tr className="border-b border-gray-100 font-bold bg-gray-50"><td className="py-1 text-black">Net Taxable Revenue</td><td className="py-1 text-right font-mono text-black">{currency} {fmt(netSales)}</td></tr>
              <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">Value Added Tax (VAT)</td><td className="py-1 text-right font-mono">+{currency} {fmt(tax)}</td></tr>
              {serviceCharge > 0 && <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">Service Charge</td><td className="py-1 text-right font-mono">+{currency} {fmt(serviceCharge)}</td></tr>}
              {roundOff !== 0 && <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">Round Off Adjustment</td><td className="py-1 text-right font-mono">{currency} {fmt(roundOff)}</td></tr>}
              {complementary > 0 && <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">Complementary Items Value</td><td className="py-1 text-right font-mono">{currency} {fmt(complementary)}</td></tr>}
              <tr className="font-black border-t-2 border-black bg-gray-100"><td className="py-1.5 text-black">TOTAL BILLED INVOICES</td><td className="py-1.5 text-right font-mono text-black">{currency} {fmt(totalBilled)}</td></tr>
            </tbody>
          </table>
        </div>

        {/* Payment Methods Table with Dedicated Subtotals */}
        <div className="mb-4">
          <div className="text-xs font-black uppercase tracking-wider text-black border-b border-gray-300 pb-1 mb-1.5 flex justify-between">
            <span>Payment Mode Breakdown & Audit</span>
            <span className="text-2xs font-bold text-emerald-800">Total Receipts: {currency} {fmt(totalCollectedReceipts)}</span>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-gray-50 text-2xs text-gray-600">
                <th className="py-1 text-left">Payment Mode</th>
                <th className="py-1 text-right">Amount Collected</th>
                <th className="py-1 text-right">% Share</th>
              </tr>
            </thead>
            <tbody>
              {consolidatedPayments.map((p, idx) => {
                const amt = Number(p.total_amount || 0);
                const share = totalCollectedReceipts > 0 ? ((amt / totalCollectedReceipts) * 100).toFixed(1) : "0.0";
                return (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="py-1 font-medium text-black">{p.payment_method || "Payment"}</td>
                    <td className="py-1 text-right font-mono font-bold">{currency} {fmt(amt)}</td>
                    <td className="py-1 text-right text-gray-600">{share}%</td>
                  </tr>
                );
              })}

              {/* DEDICATED CASH & QR SUMMARY ROWS */}
              <tr className="border-t-2 border-emerald-400 bg-emerald-50 text-emerald-950 font-bold">
                <td className="py-1.5 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-600 inline-block"></span>
                  TOTAL CASH COLLECTED
                </td>
                <td className="py-1.5 text-right font-mono font-black text-emerald-900">
                  {currency} {fmt(totalCashCollected)}
                </td>
                <td className="py-1.5 text-right text-xs">
                  {totalCollectedReceipts > 0 ? ((totalCashCollected / totalCollectedReceipts) * 100).toFixed(1) : 0}%
                </td>
              </tr>

              <tr className="border-b border-indigo-300 bg-indigo-50 text-indigo-950 font-bold">
                <td className="py-1.5 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-indigo-600 inline-block"></span>
                  TOTAL QR / DIGITAL COLLECTED
                </td>
                <td className="py-1.5 text-right font-mono font-black text-indigo-900">
                  {currency} {fmt(totalQrCollected)}
                </td>
                <td className="py-1.5 text-right text-xs">
                  {totalCollectedReceipts > 0 ? ((totalQrCollected / totalCollectedReceipts) * 100).toFixed(1) : 0}%
                </td>
              </tr>

              {totalCardCollected > 0 && (
                <tr className="border-b border-blue-200 bg-blue-50 text-blue-950 font-bold">
                  <td className="py-1 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-600 inline-block"></span>
                    TOTAL CARD COLLECTED
                  </td>
                  <td className="py-1 text-right font-mono font-bold text-blue-900">
                    {currency} {fmt(totalCardCollected)}
                  </td>
                  <td className="py-1 text-right text-xs">
                    {totalCollectedReceipts > 0 ? ((totalCardCollected / totalCollectedReceipts) * 100).toFixed(1) : 0}%
                  </td>
                </tr>
              )}

              {/* GRAND TOTAL RECEIPTS ROW */}
              <tr className="border-t-2 border-black bg-gray-100 font-black text-black">
                <td className="py-2 uppercase tracking-wide">GRAND TOTAL COLLECTED RECEIPTS</td>
                <td className="py-2 text-right font-mono text-sm font-black text-black">
                  {currency} {fmt(totalCollectedReceipts)}
                </td>
                <td className="py-2 text-right font-bold">100.0%</td>
              </tr>

              {duesCreated > 0 && (
                <tr className="border-b border-amber-200 bg-amber-50 text-amber-900 font-bold">
                  <td className="py-1.5">Unsettled Credit / Sales Due Created</td>
                  <td className="py-1.5 text-right font-mono font-bold">{currency} {fmt(duesCreated)}</td>
                  <td className="py-1.5 text-right text-amber-800 text-3xs uppercase">Credit Due</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Itemized Transactions Table (if available) */}
        {transactions && transactions.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-black uppercase tracking-wider text-black border-b border-gray-300 pb-1 mb-1.5 flex justify-between">
              <span>Itemized Invoices Audit</span>
              <span className="text-2xs font-normal text-gray-500">Showing {Math.min(transactions.length, 30)} of {transactions.length}</span>
            </div>
            <table className="w-full text-2xs">
              <thead>
                <tr className="border-b bg-gray-100 text-gray-700">
                  <th className="py-1 px-1 text-left">Time</th>
                  <th className="py-1 px-1 text-left">Invoice #</th>
                  <th className="py-1 px-1 text-left">Customer</th>
                  <th className="py-1 px-1 text-left">Settled By</th>
                  <th className="py-1 px-1 text-left">Method</th>
                  <th className="py-1 px-1 text-right">Gross</th>
                  <th className="py-1 px-1 text-right">Tax</th>
                  <th className="py-1 px-1 text-right">Total</th>
                  <th className="py-1 px-1 text-right">Due</th>
                </tr>
              </thead>
              <tbody>
                {transactions.slice(0, 30).map((inv, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="py-1 px-1 text-gray-600">{inv.created_at ? new Date(inv.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "-"}</td>
                    <td className="py-1 px-1 font-mono font-bold">{inv.invoice_no || `#${inv.id}`}</td>
                    <td className="py-1 px-1 truncate max-w-[100px]">{inv.customer_name || inv.customer_id || "Walk-in"}</td>
                    <td className="py-1 px-1 text-indigo-900 font-medium">{staffProfileName}</td>
                    <td className="py-1 px-1">{inv.payment_method_name || inv.payment_method || "Cash"}</td>
                    <td className="py-1 px-1 text-right font-mono">{fmt(inv.sub_total)}</td>
                    <td className="py-1 px-1 text-right font-mono">{fmt(inv.tax_total)}</td>
                    <td className="py-1 px-1 text-right font-mono font-bold">{fmt(inv.total)}</td>
                    <td className="py-1 px-1 text-right font-mono text-amber-700">{Number(inv.due_amount) > 0 ? fmt(inv.due_amount) : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Dual Signatures */}
        <div className="grid grid-cols-2 gap-8 pt-8 mt-6 border-t border-gray-400 text-xs">
          <div className="text-center">
            <div className="border-b border-black mb-1 w-3/4 mx-auto"></div>
            <div className="font-bold text-black">{staffProfileName}</div>
            <div className="text-2xs text-gray-600">Prepared & Settled By ({staffDesignation})</div>
          </div>
          <div className="text-center">
            <div className="border-b border-black mb-1 w-3/4 mx-auto"></div>
            <div className="font-bold text-black">Authorized Signatory</div>
            <div className="text-2xs text-gray-600">Accounts & Internal Audit</div>
          </div>
        </div>

        <div className="text-center text-3xs text-gray-500 mt-6 pt-2 border-t border-dashed">
          Official Sales Audit Document | Printed: {new Date().toLocaleString()} | User: {staffProfileName}
        </div>
      </div>
    );
  };

  // ==========================================
  // 4. PURCHASE AUDIT REPORT
  // ==========================================
  const renderPurchaseReport = () => {
    const purchases = data?.purchases || [];
    const purchasePayments = data?.purchase_payments || [];
    const totalPurchases = Number(data?.total_purchases || purchases.reduce((acc, c) => acc + Number(c.total_amount || 0), 0));
    const totalPurchaseDue = Number(data?.total_purchase_due || purchases.reduce((acc, c) => acc + Number(c.due_amount || 0), 0));
    const totalPaidPurchases = purchases.reduce((acc, c) => acc + Number(c.paid_amount || 0), 0);

    return (
      <div className="report-paper">
        {/* Header */}
        <div className="text-center pb-3 border-b-2 border-dashed border-gray-400 mb-4">
          <h2 className="text-xl font-black uppercase tracking-wider text-black">{storeName}</h2>
          {storeAddress && <p className="text-xs text-gray-700">{storeAddress}</p>}
          <div className="flex justify-center gap-3 text-xs text-gray-700 mt-0.5">
            {storePhone && <span>Tel: {storePhone}</span>}
            {storePan && <span>PAN/VAT: {storePan}</span>}
          </div>
          <div className="mt-2.5 inline-block px-3 py-1 bg-black text-white text-xs font-black uppercase tracking-widest rounded-xs">
            PURCHASE EXPENDITURE AUDIT REPORT
          </div>
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-2 gap-2 text-xs border-b border-gray-300 pb-2.5 mb-3">
          <div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Period:</span><span className="font-bold text-black">{dateRange.from || "Today"} {dateRange.to && dateRange.to !== dateRange.from ? `to ${dateRange.to}` : ""}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Total Purchase Records:</span><span className="font-bold text-black">{purchases.length} Invoices</span></div>
          </div>
          <div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Logged Staff (Profile):</span><span className="font-bold text-indigo-900">{staffProfileName}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Department:</span><span>Inventory & Procurement</span></div>
          </div>
        </div>

        {/* Highlights */}
        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
          <div className="bg-gray-100 p-2.5 rounded border">
            <div className="text-3xs uppercase font-bold text-gray-600">Total Purchase Billed</div>
            <div className="text-base font-black text-black">{currency} {fmt(totalPurchases)}</div>
          </div>
          <div className="bg-emerald-50 p-2.5 rounded border border-emerald-300">
            <div className="text-3xs uppercase font-bold text-emerald-800">Total Paid to Suppliers</div>
            <div className="text-base font-black text-emerald-900">{currency} {fmt(totalPaidPurchases)}</div>
          </div>
          <div className="bg-amber-50 p-2.5 rounded border border-amber-300">
            <div className="text-3xs uppercase font-bold text-amber-800">Outstanding Supplier Dues</div>
            <div className="text-base font-black text-amber-900">{currency} {fmt(totalPurchaseDue)}</div>
          </div>
        </div>

        {/* Purchase Payments Mode Breakdown */}
        {purchasePayments && purchasePayments.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-black uppercase tracking-wider text-black border-b border-gray-300 pb-1 mb-1.5 flex justify-between">
              <span>Disbursements by Payment Mode</span>
              <span className="text-2xs font-normal text-gray-500">Cash vs Bank</span>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-gray-50 text-2xs text-gray-600">
                  <th className="py-1 text-left">Payment Mode</th>
                  <th className="py-1 text-right">Disbursed Amount</th>
                </tr>
              </thead>
              <tbody>
                {purchasePayments.map((pp, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="py-1 font-medium">{pp.payment_method || "Cash"}</td>
                    <td className="py-1 text-right font-mono font-bold text-rose-700">-{currency} {fmt(pp.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Itemized Purchases List */}
        <div className="mb-4">
          <div className="text-xs font-black uppercase tracking-wider text-black border-b border-gray-300 pb-1 mb-1.5 flex justify-between">
            <span>Itemized Supplier Purchases</span>
            <span className="text-2xs font-normal text-gray-500">List of Orders</span>
          </div>
          {purchases.length > 0 ? (
            <table className="w-full text-2xs">
              <thead>
                <tr className="border-b bg-gray-100 text-gray-700">
                  <th className="py-1 px-1 text-left">Date</th>
                  <th className="py-1 px-1 text-left">Invoice / PO #</th>
                  <th className="py-1 px-1 text-left">Supplier / Vendor</th>
                  <th className="py-1 px-1 text-left">Phone</th>
                  <th className="py-1 px-1 text-left">Recorded By</th>
                  <th className="py-1 px-1 text-right">Total</th>
                  <th className="py-1 px-1 text-right">Paid</th>
                  <th className="py-1 px-1 text-right">Due</th>
                  <th className="py-1 px-1 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((p, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="py-1 px-1 text-gray-600">{p.purchase_date ? String(p.purchase_date).substring(0, 10) : "-"}</td>
                    <td className="py-1 px-1 font-mono font-bold">{p.purchase_invoice_no || `#${p.id}`}</td>
                    <td className="py-1 px-1 font-medium text-black truncate max-w-[120px]">{p.supplier_name || "Supplier"}</td>
                    <td className="py-1 px-1 text-gray-600">{p.supplier_phone || "-"}</td>
                    <td className="py-1 px-1 text-indigo-900 font-medium">{staffProfileName}</td>
                    <td className="py-1 px-1 text-right font-mono font-bold">{fmt(p.total_amount)}</td>
                    <td className="py-1 px-1 text-right font-mono text-emerald-700">{fmt(p.paid_amount)}</td>
                    <td className="py-1 px-1 text-right font-mono text-amber-700">{fmt(p.due_amount)}</td>
                    <td className="py-1 px-1 text-center font-bold uppercase text-3xs">{p.payment_status || "Completed"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-4 text-center border border-dashed text-xs text-gray-500 bg-gray-50">
              No purchase orders recorded for the selected period.
            </div>
          )}
        </div>

        {/* Dual Signatures */}
        <div className="grid grid-cols-2 gap-8 pt-8 mt-6 border-t border-gray-400 text-xs">
          <div className="text-center">
            <div className="border-b border-black mb-1 w-3/4 mx-auto"></div>
            <div className="font-bold text-black">{staffProfileName}</div>
            <div className="text-2xs text-gray-600">Procurement Staff Signature</div>
          </div>
          <div className="text-center">
            <div className="border-b border-black mb-1 w-3/4 mx-auto"></div>
            <div className="font-bold text-black">Store Manager / Auditor</div>
            <div className="text-2xs text-gray-600">Verification & Approval</div>
          </div>
        </div>

        <div className="text-center text-3xs text-gray-500 mt-6 pt-2 border-t border-dashed">
          Official Purchase Audit Document | Printed: {new Date().toLocaleString()} | User: {staffProfileName}
        </div>
      </div>
    );
  };

  // ==========================================
  // 5. EXPENSES AUDIT REPORT
  // ==========================================
  const renderExpensesReport = () => {
    const expenses = data?.expenses || [];
    const totalExpenses = Number(data?.total_expenses || expenses.reduce((acc, c) => acc + Number(c.amount || 0), 0));
    const totalExpenseDue = Number(data?.total_expense_due || expenses.reduce((acc, c) => acc + Number(c.due_amount || 0), 0));
    const paidExpenses = expenses.reduce((acc, c) => acc + Number(c.paid_amount || c.amount || 0), 0);

    return (
      <div className="report-paper">
        {/* Header */}
        <div className="text-center pb-3 border-b-2 border-dashed border-gray-400 mb-4">
          <h2 className="text-xl font-black uppercase tracking-wider text-black">{storeName}</h2>
          {storeAddress && <p className="text-xs text-gray-700">{storeAddress}</p>}
          <div className="flex justify-center gap-3 text-xs text-gray-700 mt-0.5">
            {storePhone && <span>Tel: {storePhone}</span>}
            {storePan && <span>PAN/VAT: {storePan}</span>}
          </div>
          <div className="mt-2.5 inline-block px-3 py-1 bg-black text-white text-xs font-black uppercase tracking-widest rounded-xs">
            EXPENSES AUDIT REPORT
          </div>
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-2 gap-2 text-xs border-b border-gray-300 pb-2.5 mb-3">
          <div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Period:</span><span className="font-bold text-black">{dateRange.from || "Today"} {dateRange.to && dateRange.to !== dateRange.from ? `to ${dateRange.to}` : ""}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Total Vouchers:</span><span className="font-bold text-black">{expenses.length} Records</span></div>
          </div>
          <div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Logged Staff (Profile):</span><span className="font-bold text-indigo-900">{staffProfileName}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Staff Designation:</span><span>{staffDesignation}</span></div>
          </div>
        </div>

        {/* Highlights */}
        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
          <div className="bg-rose-50 p-2.5 rounded border border-rose-300">
            <div className="text-3xs uppercase font-bold text-rose-800">Total Expenses</div>
            <div className="text-base font-black text-rose-900">{currency} {fmt(totalExpenses)}</div>
          </div>
          <div className="bg-emerald-50 p-2.5 rounded border border-emerald-300">
            <div className="text-3xs uppercase font-bold text-emerald-800">Total Cash / Disbursed</div>
            <div className="text-base font-black text-emerald-900">{currency} {fmt(paidExpenses)}</div>
          </div>
          <div className="bg-amber-50 p-2.5 rounded border border-amber-300">
            <div className="text-3xs uppercase font-bold text-amber-800">Unsettled Expense Dues</div>
            <div className="text-base font-black text-amber-900">{currency} {fmt(totalExpenseDue)}</div>
          </div>
        </div>

        {/* Itemized Expenses List */}
        <div className="mb-4">
          <div className="text-xs font-black uppercase tracking-wider text-black border-b border-gray-300 pb-1 mb-1.5 flex justify-between">
            <span>Itemized Expense Vouchers</span>
            <span className="text-2xs font-normal text-gray-500">Expenditure Log</span>
          </div>
          {expenses.length > 0 ? (
            <table className="w-full text-2xs">
              <thead>
                <tr className="border-b bg-gray-100 text-gray-700">
                  <th className="py-1 px-1 text-left">Date</th>
                  <th className="py-1 px-1 text-left">Category</th>
                  <th className="py-1 px-1 text-left">Particulars / Note</th>
                  <th className="py-1 px-1 text-left">Payee / Supplier</th>
                  <th className="py-1 px-1 text-left">Mode</th>
                  <th className="py-1 px-1 text-left">Authorized By</th>
                  <th className="py-1 px-1 text-right">Amount</th>
                  <th className="py-1 px-1 text-right">Paid</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="py-1 px-1 text-gray-600">{e.date ? String(e.date).substring(0, 10) : "-"}</td>
                    <td className="py-1 px-1 font-bold text-black">{e.category_name || "General"}</td>
                    <td className="py-1 px-1 text-gray-800 truncate max-w-[120px]">{e.description || "-"}</td>
                    <td className="py-1 px-1 text-gray-700">{e.supplier_name || "-"}</td>
                    <td className="py-1 px-1">{e.payment_method || "Cash"}</td>
                    <td className="py-1 px-1 text-indigo-900 font-medium">{staffProfileName}</td>
                    <td className="py-1 px-1 text-right font-mono font-bold text-rose-700">-{fmt(e.amount)}</td>
                    <td className="py-1 px-1 text-right font-mono text-black">{fmt(e.paid_amount || e.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-4 text-center border border-dashed text-xs text-gray-500 bg-gray-50">
              No expense vouchers recorded for the selected period.
            </div>
          )}
        </div>

        {/* Dual Signatures */}
        <div className="grid grid-cols-2 gap-8 pt-8 mt-6 border-t border-gray-400 text-xs">
          <div className="text-center">
            <div className="border-b border-black mb-1 w-3/4 mx-auto"></div>
            <div className="font-bold text-black">{staffProfileName}</div>
            <div className="text-2xs text-gray-600">Disbursed / Authorized By ({staffDesignation})</div>
          </div>
          <div className="text-center">
            <div className="border-b border-black mb-1 w-3/4 mx-auto"></div>
            <div className="font-bold text-black">Managing Director / Auditor</div>
            <div className="text-2xs text-gray-600">Verification & Sanction</div>
          </div>
        </div>

        <div className="text-center text-3xs text-gray-500 mt-6 pt-2 border-t border-dashed">
          Official Expenses Audit Document | Printed: {new Date().toLocaleString()} | User: {staffProfileName}
        </div>
      </div>
    );
  };

  // ==========================================
  // 6. REGISTER REPORT (DAILY REGISTER AUDIT)
  // ==========================================
  const renderRegisterReport = () => {
    const regData = selectedRegister || data?.registerSummary || data || {};
    const shifts = regData.shifts || [];
    const invoices = regData.invoices || {};
    const payments = regData.payments || [];
    const expenses = regData.expenses || [];
    const transactions = regData.transactions || [];

    const grossSales = Number(invoices.total_gross || 0);
    const discount = Number(invoices.total_discount || 0);
    const netSales = Number(invoices.net_sales || grossSales - discount);
    const tax = Number(invoices.total_tax || 0);
    const roundOff = Number(invoices.total_round_off || 0);
    const totalBilled = Number(invoices.total_billed || netSales + tax);
    const duesCreated = Number(invoices.due_sales || 0);

    return (
      <div className="report-paper">
        {/* Header */}
        <div className="text-center pb-3 border-b-2 border-dashed border-gray-400 mb-4">
          <h2 className="text-xl font-black uppercase tracking-wider text-black">{storeName}</h2>
          {storeAddress && <p className="text-xs text-gray-700">{storeAddress}</p>}
          <div className="flex justify-center gap-3 text-xs text-gray-700 mt-0.5">
            {storePhone && <span>Tel: {storePhone}</span>}
            {storePan && <span>PAN/VAT: {storePan}</span>}
          </div>
          <div className="mt-2.5 inline-block px-3 py-1 bg-black text-white text-xs font-black uppercase tracking-widest rounded-xs">
            DAILY REGISTER AUDIT REPORT
          </div>
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-2 gap-2 text-xs border-b border-gray-300 pb-2.5 mb-3">
          <div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Register Date:</span><span className="font-bold text-black">{dateRange.from || "Today"}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Total Shifts:</span><span className="font-bold text-black">{shifts.length} Shifts</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Total Bills Settled:</span><span className="font-bold text-black">{transactions.length || 0} Bills</span></div>
          </div>
          <div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Audit Certified By:</span><span className="font-bold text-indigo-900">{staffProfileName}</span></div>
            <div className="flex justify-between py-0.5"><span className="text-gray-600">Terminal:</span><span>MAIN CASH REGISTER</span></div>
          </div>
        </div>

        {/* Shift History Table */}
        <div className="mb-4">
          <div className="text-xs font-black uppercase tracking-wider text-black border-b border-gray-300 pb-1 mb-1.5 flex justify-between">
            <span>Shifts Executed During Register Day</span>
            <span className="text-2xs font-normal text-gray-500">Cashier Audit</span>
          </div>
          {shifts.length > 0 ? (
            <table className="w-full text-2xs">
              <thead>
                <tr className="border-b bg-gray-100 text-gray-700">
                  <th className="py-1 px-1 text-left">Shift #</th>
                  <th className="py-1 px-1 text-left">Cashier</th>
                  <th className="py-1 px-1 text-left">Opened</th>
                  <th className="py-1 px-1 text-left">Closed</th>
                  <th className="py-1 px-1 text-right">Opening Float</th>
                  <th className="py-1 px-1 text-right">Expected Cash</th>
                  <th className="py-1 px-1 text-right">Actual Count</th>
                  <th className="py-1 px-1 text-right">Variance</th>
                </tr>
              </thead>
              <tbody>
                {shifts.map((s, idx) => {
                  const sDiff = Number(s.cash_difference || (s.closing_balance - s.system_expected_cash) || 0);
                  return (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="py-1 px-1 font-bold">Shift #{s.shift_number || s.id}</td>
                      <td className="py-1 px-1 font-medium text-black">{s.cashier_name || s.opened_by_name || "Cashier"}</td>
                      <td className="py-1 px-1 text-gray-600">{s.opened_at ? new Date(s.opened_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "-"}</td>
                      <td className="py-1 px-1 text-gray-600">{s.closed_at ? new Date(s.closed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "Active"}</td>
                      <td className="py-1 px-1 text-right font-mono">{fmt(s.opening_balance)}</td>
                      <td className="py-1 px-1 text-right font-mono">{fmt(s.system_expected_cash)}</td>
                      <td className="py-1 px-1 text-right font-mono font-bold">{fmt(s.closing_balance || s.actual_cash_in_hand)}</td>
                      <td className={`py-1 px-1 text-right font-mono font-bold ${Math.abs(sDiff) < 0.01 ? "text-emerald-700" : sDiff > 0 ? "text-blue-700" : "text-rose-700"}`}>
                        {sDiff > 0 ? `+${fmt(sDiff)}` : fmt(sDiff)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="p-3 text-center border border-dashed rounded text-xs text-gray-500 bg-gray-50">
              No shift records logged for this register business date.
            </div>
          )}
        </div>

        {/* Register Totals Breakdown */}
        <div className="mb-4">
          <div className="text-xs font-black uppercase tracking-wider text-black border-b border-gray-300 pb-1 mb-1.5">
            Register Day Sales & Tax Summary
          </div>
          <table className="w-full text-xs">
            <tbody>
              <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">Gross Sales</td><td className="py-1 text-right font-mono">{currency} {fmt(grossSales)}</td></tr>
              <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">Discounts Concession</td><td className="py-1 text-right font-mono text-rose-600">-{currency} {fmt(discount)}</td></tr>
              <tr className="border-b border-gray-100 font-bold bg-gray-50"><td className="py-1 text-black">Net Sales</td><td className="py-1 text-right font-mono text-black">{currency} {fmt(netSales)}</td></tr>
              <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">VAT / Tax Total</td><td className="py-1 text-right font-mono">+{currency} {fmt(tax)}</td></tr>
              <tr className="font-black border-t-2 border-black bg-gray-100"><td className="py-1.5 text-black">TOTAL REGISTER BILLED</td><td className="py-1.5 text-right font-mono text-black">{currency} {fmt(totalBilled)}</td></tr>
              {duesCreated > 0 && <tr className="border-t border-amber-200 bg-amber-50 text-amber-900 font-bold"><td className="py-1">New Credit Dues Created</td><td className="py-1 text-right font-mono">{currency} {fmt(duesCreated)}</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Collections Breakdown */}
        <div className="mb-4">
          <div className="text-xs font-black uppercase tracking-wider text-black border-b border-gray-300 pb-1 mb-1.5 flex justify-between">
            <span>Payment Mode Receipts</span>
            <span>Audited Collections</span>
          </div>
          <table className="w-full text-xs">
            <tbody>
              {payments.map((p, idx) => (
                <tr key={idx} className="border-b border-gray-100">
                  <td className="py-1 font-medium">{p.payment_method || "Payment"}</td>
                  <td className="py-1 text-right font-mono font-bold">{currency} {fmt(p.total || p.amount || 0)}</td>
                </tr>
              ))}

              {(() => {
                let regCash = 0;
                let regQr = 0;
                let regTotal = 0;
                payments.forEach(p => {
                  const amt = Number(p.total || p.total_amount || p.amount || 0);
                  regTotal += amt;
                  const m = (p.payment_method || '').toLowerCase();
                  if (m.includes('cash')) regCash += amt;
                  else if (m.includes('qr') || m.includes('fonepay') || m.includes('esewa') || m.includes('khalti') || m.includes('online')) regQr += amt;
                });
                return (
                  <>
                    <tr className="border-t-2 border-emerald-400 bg-emerald-50 text-emerald-950 font-bold">
                      <td className="py-1">💵 TOTAL CASH COLLECTED</td>
                      <td className="py-1 text-right font-mono font-black text-emerald-900">{currency} {fmt(regCash)}</td>
                    </tr>
                    <tr className="border-b border-indigo-300 bg-indigo-50 text-indigo-950 font-bold">
                      <td className="py-1">📱 TOTAL QR / DIGITAL COLLECTED</td>
                      <td className="py-1 text-right font-mono font-black text-indigo-900">{currency} {fmt(regQr)}</td>
                    </tr>
                    <tr className="border-t-2 border-black bg-gray-100 font-black text-black">
                      <td className="py-1.5 uppercase">GRAND TOTAL REGISTER RECEIPTS</td>
                      <td className="py-1.5 text-right font-mono text-sm font-black text-black">{currency} {fmt(regTotal)}</td>
                    </tr>
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>

        {/* Dual Signatures */}
        <div className="grid grid-cols-2 gap-8 pt-8 mt-6 border-t border-gray-400 text-xs">
          <div className="text-center">
            <div className="border-b border-black mb-1 w-3/4 mx-auto"></div>
            <div className="font-bold text-black">{staffProfileName}</div>
            <div className="text-2xs text-gray-600">Chief Cashier / Auditor ({staffDesignation})</div>
          </div>
          <div className="text-center">
            <div className="border-b border-black mb-1 w-3/4 mx-auto"></div>
            <div className="font-bold text-black">General Manager</div>
            <div className="text-2xs text-gray-600">Final Audit Verification</div>
          </div>
        </div>

        <div className="text-center text-3xs text-gray-500 mt-6 pt-2 border-t border-dashed">
          Daily Register Audit Document | Printed: {new Date().toLocaleString()} | User: {staffProfileName}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* CSS STYLES FOR PRINTING & SCREEN PREVIEW */}
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          #financial-printable-report,
          #financial-printable-report * {
            visibility: visible !important;
          }
          #financial-printable-report {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: ${format === "80mm" ? "80mm" : "100%"} !important;
            margin: 0 !important;
            padding: ${format === "80mm" ? "5mm" : "15mm"} !important;
            background: #fff !important;
            color: #000 !important;
            box-shadow: none !important;
          }
          .no-print,
          .modal-backdrop,
          .navbar,
          .sidebar,
          header,
          footer {
            display: none !important;
          }
          @page {
            size: ${format === "80mm" ? "80mm auto" : "A4 portrait"};
            margin: ${format === "80mm" ? "2mm" : "10mm"};
          }
        }

        .report-paper {
          background: #ffffff;
          color: #111827;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          line-height: 1.4;
        }

        .format-80mm {
          max-width: 80mm;
          margin: 0 auto;
          font-size: 11px;
          padding: 12px;
        }

        .format-a4 {
          max-width: 800px;
          margin: 0 auto;
          padding: 24px;
        }
      `}</style>

      {/* MODAL OVERLAY */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden max-h-[95vh] w-full max-w-4xl border border-gray-200"
          style={{ pointerEvents: "auto" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* TOP BAR / CONTROLS (NO-PRINT) */}
          <div className="no-print bg-slate-900 text-white px-5 py-3.5 flex items-center justify-between border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-600 rounded-lg text-white">
                <IconPrinter size={20} stroke={iconStroke} />
              </div>
              <div>
                <h3 className="font-bold text-base tracking-wide flex items-center gap-2 text-white">
                  {reportType === "sales" && "Professional Sales Report"}
                  {reportType === "purchase" && "Professional Purchase Report"}
                  {reportType === "expenses" && "Professional Expenses Report"}
                  {reportType === "register" && "Professional Register Report"}
                  {reportType === "shift_open" && "Shift Open & Float Report"}
                  {reportType === "shift_close" && "Shift Close Z-Report"}
                  {reportType === "shift_full" && "Full Shift Statement"}
                </h3>
                <p className="text-2xs text-slate-300">
                  Certified by: <strong className="text-emerald-400">{staffProfileName}</strong> ({staffDesignation})
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Format Switcher */}
              <div className="flex bg-slate-800 p-0.5 rounded-lg border border-slate-700 text-xs">
                <button
                  type="button"
                  onClick={() => setFormat("a4")}
                  className={`px-3 py-1 rounded-md font-semibold flex items-center gap-1.5 transition-all ${format === "a4" ? "bg-indigo-600 text-white shadow-xs" : "text-slate-300 hover:text-white"}`}
                >
                  <IconFileText size={14} /> A4 Sheet
                </button>
                <button
                  type="button"
                  onClick={() => setFormat("80mm")}
                  className={`px-3 py-1 rounded-md font-semibold flex items-center gap-1.5 transition-all ${format === "80mm" ? "bg-indigo-600 text-white shadow-xs" : "text-slate-300 hover:text-white"}`}
                >
                  <IconReceipt size={14} /> 80mm POS Slip
                </button>
              </div>

              {/* Print Button */}
              <button
                type="button"
                onClick={handlePrint}
                className="btn btn-sm bg-emerald-600 hover:bg-emerald-500 text-white border-0 px-4 font-bold flex items-center gap-1.5 shadow-md"
              >
                <IconPrinter size={16} /> Print Report
              </button>

              {/* Close Button */}
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <IconX size={20} />
              </button>
            </div>
          </div>

          {/* DOCUMENT PREVIEW CONTAINER */}
          <div className="overflow-y-auto p-4 md:p-8 bg-slate-100 flex justify-center items-start flex-1 min-h-[400px]">
            <div
              id="financial-printable-report"
              className={`bg-white shadow-lg border border-gray-300 rounded-lg ${format === "80mm" ? "format-80mm" : "format-a4"}`}
            >
              {renderReportContent()}
            </div>
          </div>

          {/* BOTTOM BAR INFO (NO-PRINT) */}
          <div className="no-print bg-slate-50 border-t px-5 py-2.5 flex justify-between items-center text-xs text-gray-500">
            <span>
              💡 <strong>Tip:</strong> Press <code>Ctrl + P</code> or click <strong>Print Report</strong>. Header/margins are optimized for thermal POS & standard laser printers.
            </span>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-sm btn-ghost text-gray-600"
            >
              Close Preview
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
